// "Proposed: <engineer>, <day> <window>. Accept" (Session SC5). ONE line and ONE button on the record where the office already
// assigns an unassigned call-out, repair or technician visit: the platform has already looked at who is certified, free, near and on
// the clock, and the person reads the answer and presses Accept instead of looking it all up and then choosing.
//
// It is DERIVED ON READ (GET /api/schedule/proposal) and stored nowhere. Accept does not write anything itself: it hands the proposal
// to the caller's own assign function, which dispatches the EXISTING route (PATCH /api/callouts/:id/assign or PATCH
// /api/schedule/jobs/:id, notifyNow off), so every guard, warning, audit row and the SLA assignedAt writer run exactly as for a hand
// assignment. Nothing is sent to the customer; the engineer is told, as for any assignment, and the line says so.
//
// A PROPOSAL CAN GO STALE while the page is open (someone else assigns the record, leave is recorded), and neither existing route
// refuses an already-assigned record: assigning would silently take it off the other engineer. So Confirm FIRST asks again, and goes
// ahead only if the answer is still "proposed" for the SAME engineer and day; otherwise nothing is sent, the new proposal is shown and
// the person is told what changed.
//
// A call-out is dispatched NOW (the assign route dates its job now and tells the engineer now), so its line says "dispatched now" and
// never a future day; only a repair or technician visit carries a day, because only the schedule route writes one.
//
// NO NEW CONTROL TO CHOOSE ANOTHER: the picker the person always had stays where it is on the same record, so "choose another" is
// simply using it. An entrapment, an already-assigned or closed record and anything that is not a call-out, repair or technician
// visit shows NOTHING (the entrapment rule). A proposal that cannot be made says why, in one quiet line, and never guesses. A proposal
// that fails to load shows nothing: today's flow is untouched.
import { useEffect, useState } from "react";
import { api, type SlotProposal } from "../lib/api";
import { buttonClasses } from "./Button";
import { ConfirmButton } from "./ConfirmButton";
import { toneTextClass } from "./StatusPill";

const SILENT_CODES = new Set(["entrapment", "already_assigned", "closed", "not_applicable"]);

export function ProposedSlotLine({
  target,
  busy = false,
  onAccept,
}: {
  target: { calloutId: string } | { jobId: string };
  busy?: boolean;
  onAccept: (proposal: SlotProposal) => void | Promise<void>;
}) {
  const [proposal, setProposal] = useState<SlotProposal | null>(null);
  const [checking, setChecking] = useState(false);
  const [changed, setChanged] = useState<string | null>(null);
  const id = "calloutId" in target ? `c:${target.calloutId}` : `j:${target.jobId}`;

  useEffect(() => {
    let cancelled = false;
    setProposal(null);
    setChanged(null);
    void (async () => {
      try {
        const p = await api.schedule.proposal(target);
        if (!cancelled) setProposal(p);
      } catch {
        // A proposal that cannot be loaded shows nothing: the person assigns by hand exactly as before.
        if (!cancelled) setProposal(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function confirm() {
    if (!proposal) return;
    setChecking(true);
    try {
      const fresh = await api.schedule.proposal(target);
      const same = fresh.status === "proposed" && fresh.engineerId === proposal.engineerId && fresh.day === proposal.day;
      if (!same) {
        // Nothing is sent. Show the new answer and say what changed.
        setProposal(fresh);
        setChanged(
          fresh.status === "proposed"
            ? `${fresh.engineerName} is now proposed instead`
            : fresh.code === "already_assigned"
              ? "it has been assigned since"
              : (fresh.reason ?? "it can no longer be proposed"),
        );
        return;
      }
      setChanged(null);
      await onAccept(fresh);
    } catch {
      setChanged("the proposal could not be checked just now, so nothing was assigned");
    } finally {
      setChecking(false);
    }
  }

  const stale = changed ? (
    <p role="alert" data-testid="proposal-changed" className={`mb-3 text-xs ${toneTextClass("amber")}`}>
      This changed: {changed}. Reload to see the new proposal.
    </p>
  ) : null;

  if (!proposal) return stale;
  if (proposal.status === "none") {
    if (proposal.code && SILENT_CODES.has(proposal.code)) return stale;
    return (
      <>
        {stale}
        <p data-testid="proposal-none" className="mb-3 text-xs text-ele-body opacity-80">
          No proposal. {proposal.reason}
        </p>
      </>
    );
  }

  const name = proposal.engineerName ?? "the engineer";
  const label = proposal.summary ?? "Proposed engineer";
  const isCallout = proposal.kind === "callout";
  const datePart = !isCallout && proposal.scheduledFor ? ` for ${proposal.dayLabel}` : isCallout ? " now" : "";
  const told = isCallout ? `${name} will be told straight away.` : `${name} will be told.`;
  return (
    <>
      {stale}
      <div data-testid="proposal-line" className="mb-3 rounded-ele border border-ele-purple-accent bg-ele-field p-3">
        <p className="text-sm text-ele-strong">{label}</p>
        {proposal.reasons && proposal.reasons.length > 0 && <p className="mt-1 text-xs text-ele-body">{proposal.reasons.join(" · ")}</p>}
        {proposal.warnings?.map((w) => (
          <p key={w} className={`mt-1 text-xs ${toneTextClass("amber")}`}>
            {w}
          </p>
        ))}
        <p className="mt-2 text-xs text-ele-strong">
          Accept assigns {name}
          {datePart}. {told} Nothing is sent to the customer.
        </p>
        <div className="mt-2">
          <ConfirmButton
            label="Accept"
            confirmLabel={`Assign ${name}?`}
            busyLabel="Assigning..."
            busy={busy || checking}
            onConfirm={() => void confirm()}
            className={buttonClasses("primary", "sm")}
            armedClassName="ring-2 ring-ele-green"
          />
        </div>
      </div>
    </>
  );
}
